import React from "react";
import ReusableDropdown from "../../../components/ReusableDropdown";
import { Mail, X, FileText, Send } from "lucide-react";

const EnhancedEmailModal = ({
  showEmailModal, selectedReport, emailForm, setEmailForm,
  emailLoading, onClose, handleShareReport,
  styles, screenSize, language, text, translateText, theme
}) => {
  if (!showEmailModal || !selectedReport) return null;

  const isMobile = screenSize?.isMobile;

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modalContainer} onClick={(e) => e.stopPropagation()}>

        {/* HEADER */}
        <div style={styles.modalHeader}>
          <div style={styles.modalTitleSection}>
            <div style={styles.modalIcon}>
              <Mail size={isMobile ? 20 : 24} />
            </div>
            <div>
              <h2 style={styles.modalTitle}>{text[language].shareReport}</h2>
              <p style={styles.modalSubtitle}>{text[language].sendReportViaEmail}</p>
            </div>
          </div>
          <button onClick={onClose} style={styles.closeButton}>
            <X size={isMobile ? 16 : 20} />
          </button>
        </div>

        {/* REPORT PREVIEW */}
        <div style={styles.reportPreview}>
          <div style={styles.previewHeader}>
            <div style={styles.previewThumbnail}>
              <FileText size={isMobile ? 20 : 24} />
            </div>
            <div style={styles.previewInfo}>
              <h3 style={styles.previewTitle}>
                {translateText(selectedReport.title, 'title')}
              </h3>
              <p style={styles.previewDescription}>
                {translateText(selectedReport.description, 'description')}
              </p>
              <div style={styles.previewMeta}>
                <span>{selectedReport.size}</span>
                <span>•</span>
                <span>{selectedReport.format}</span>
                <span>•</span>
                <span>{translateText(selectedReport.author, 'author')}</span>
              </div>
            </div>
          </div>
        </div>

        {/* FORM */}
        <div style={styles.emailForm}>

          {/* Recipients */}
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>{text[language].recipients}</label>
            <div style={styles.recipientTags}>
              {emailForm.recipients.map((email, index) => (
                <div key={index} style={styles.recipientTag}>
                  <span>{email}</span>
                  <button
                    onClick={() => setEmailForm(prev => ({
                      ...prev,
                      recipients: prev.recipients.filter((_, i) => i !== index)
                    }))}
                    style={styles.removeRecipient}
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
              <input
                type="email"
                placeholder={text[language].addRecipient}
                style={styles.recipientInput}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && e.target.value) {
                    const email = e.target.value.trim();
                    if (email) {
                      setEmailForm(prev => ({
                        ...prev,
                        recipients: [...prev.recipients, email]
                      }));
                      e.target.value = '';
                    }
                  }
                }}
              />
            </div>
          </div>

          {/* Subject */}
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>{text[language].subject}</label>
            <input
              type="text"
              value={emailForm.subject}
              onChange={(e) => setEmailForm(prev => ({ ...prev, subject: e.target.value }))}
              placeholder={`${text[language].shareReport}: ${selectedReport.title}`}
              style={styles.formInput}
            />
          </div>

          {/* Message */}
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>{text[language].message}</label>
            <textarea
              value={emailForm.message}
              onChange={(e) => setEmailForm(prev => ({ ...prev, message: e.target.value }))}
              placeholder={text[language].addPersonalMessage}
              rows={4}
              style={styles.formTextarea}
            />
          </div>

          {/* Include Attachment */}
          <div style={styles.formGroup}>
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={emailForm.includeAttachment}
                onChange={(e) => setEmailForm(prev => ({ ...prev, includeAttachment: e.target.checked }))}
                style={styles.checkbox}
              />
              <span>{text[language].includeAttachment}</span>
            </label>
          </div>

          {/* Format */}
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>Format</label>
            <ReusableDropdown
              items={[
                { key: "pdf", label: "PDF" },
                { key: "xlsx", label: "Excel" }
              ]}
              value={emailForm.format === "xlsx" ? "Excel" : "PDF"}
              onSelect={(item) => setEmailForm(prev => ({ ...prev, format: item.key }))}
              getItemKey={item => item.key}
              getItemLabel={item => item.label}
              theme={theme}
              language={language}
              inputStyle={styles.formInput}
              showClear={false}
              maxItems={2}
            />
          </div>

        </div>

        {/* ACTIONS */}
        <div style={styles.modalActions}>
          <button onClick={onClose} style={styles.cancelButton}>
            {text[language].cancel}
          </button>
          <button
            onClick={() => handleShareReport(selectedReport, false)}
            disabled={emailLoading}
            style={styles.notifyButton}
          >
            <Send size={isMobile ? 14 : 16} />
            {emailLoading ? text[language].sending : text[language].sendNotification}
          </button>
          {emailForm.includeAttachment && (
            <button
              onClick={() => handleShareReport(selectedReport, true)}
              disabled={emailLoading}
              style={styles.attachButton}
            >
              <Mail size={isMobile ? 14 : 16} />
              {emailLoading ? text[language].sending : text[language].sendWithAttachment}
            </button>
          )}
        </div>

      </div>
    </div>
  );
};

export default EnhancedEmailModal;